import { parseDateOnly, startOfDay } from '@/lib/dates';
import type { ScheduleEntryRow } from '@/types/database';

export { parseDateOnly } from '@/lib/dates';

export type ScheduleBucket =
  | 'overdue'
  | 'this_week'
  | 'next_week'
  | 'this_month'
  | 'later'
  | 'undated'
  | 'complete';

export const BUCKET_ORDER: ScheduleBucket[] = [
  'overdue', 'this_week', 'next_week', 'this_month', 'later', 'undated', 'complete',
];

export const BUCKET_LABEL: Record<ScheduleBucket, string> = {
  overdue:    'Overdue',
  this_week:  'This week',
  next_week:  'Next week',
  this_month: 'Later this month',
  later:      'Later',
  undated:    'No date set',
  complete:   'Complete',
};

export type GroupedSchedule = Record<ScheduleBucket, ScheduleEntryRow[]>;


/** Sunday-ending "week": startOfWeek returns Monday, endOfWeek returns Sunday
 *  23:59:59 of the current week containing `today`. */
function startOfWeek(today: Date): Date {
  // 0=Sun, 1=Mon, ..., 6=Sat. We want Monday as start.
  const dow = today.getDay();
  const daysBack = (dow + 6) % 7; // Mon=0 → 0 back; Sun=0 → 6 back
  const d = startOfDay(today);
  d.setDate(d.getDate() - daysBack);
  return d;
}
function addDays(d: Date, n: number): Date {
  const c = new Date(d);
  c.setDate(c.getDate() + n);
  return c;
}
function endOfMonth(today: Date): Date {
  return new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59, 999);
}

/**
 * Sort entries into relative-time buckets. `today` defaults to `new Date()`
 * but is a parameter so tests are deterministic.
 */
export function bucketize(
  entries: ScheduleEntryRow[],
  today: Date = new Date(),
): GroupedSchedule {
  const groups: GroupedSchedule = {
    overdue: [], this_week: [], next_week: [],
    this_month: [], later: [], undated: [], complete: [],
  };

  const todayStart = startOfDay(today);
  const thisWeekStart = startOfWeek(today);
  const thisWeekEnd = addDays(thisWeekStart, 7); // exclusive
  const nextWeekEnd = addDays(thisWeekEnd, 7);   // exclusive
  const monthEnd = endOfMonth(today);

  for (const e of entries) {
    // Complete + skipped go to the audit-log bucket regardless of date.
    if (e.status === 'complete' || e.status === 'skipped') {
      groups.complete.push(e);
      continue;
    }
    if (!e.start_date) {
      groups.undated.push(e);
      continue;
    }
    const d = parseDateOnly(e.start_date);
    if (Number.isNaN(d.getTime())) {
      groups.undated.push(e);
      continue;
    }
    if (d < todayStart) {
      groups.overdue.push(e);
    } else if (d < thisWeekEnd) {
      groups.this_week.push(e);
    } else if (d < nextWeekEnd) {
      groups.next_week.push(e);
    } else if (d <= monthEnd) {
      groups.this_month.push(e);
    } else {
      groups.later.push(e);
    }
  }

  // Sort each bucket chronologically. Undated stays as-inserted.
  const byDate = (a: ScheduleEntryRow, b: ScheduleEntryRow): number => {
    if (!a.start_date && !b.start_date) return 0;
    if (!a.start_date) return 1;
    if (!b.start_date) return -1;
    return a.start_date < b.start_date ? -1 : a.start_date > b.start_date ? 1 : 0;
  };
  (['overdue', 'this_week', 'next_week', 'this_month', 'later', 'complete'] as ScheduleBucket[])
    .forEach((b) => groups[b].sort(byDate));

  return groups;
}

/**
 * Format the header of the "Next week" group as an explicit date range —
 * "Jun 2 – Jun 8" — so the designer knows what "next week" actually means.
 */
export function nextWeekRangeLabel(today: Date = new Date()): string {
  const start = addDays(startOfWeek(today), 7);
  const end = addDays(start, 6);
  const fmt = (d: Date): string =>
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${fmt(start)} – ${fmt(end)}`;
}

/**
 * The next N schedule entries, soonest first — the panel that replaced
 * Milestones on the dashboard (Phase 18, decisions log #5).
 *
 * "Upcoming" means work that is still ahead or in flight:
 *   • finished and abandoned work is excluded (complete, skipped) — a done task
 *     is not something to look at next
 *   • an entry still running is included even though it started in the past,
 *     because an install that began yesterday is exactly what you want to see
 *   • undated entries sort last: they're real work, but they can't be "next"
 *
 * Dates are date-only columns, so comparisons go through parseDateOnly rather
 * than `new Date()` — see @/lib/dates.
 */
export type UpcomingCandidate =
  Pick<ScheduleEntryRow, 'task' | 'status' | 'start_date' | 'end_date'>;

// Generic over the row type: the designer's dashboard passes full rows, the
// client portal passes the narrower shape its RPC returns, and both get their
// own type back. Constrained to the four fields this actually reads.
export function upcomingEntries<T extends UpcomingCandidate>(
  entries: T[],
  opts: { limit?: number; today?: Date } = {},
): T[] {
  const limit = opts.limit ?? 8;
  const today = startOfDay(opts.today ?? new Date());

  const live = entries.filter((e) => e.status !== 'complete' && e.status !== 'skipped');

  const sortKey = (e: T): number | null => {
    // An entry with an end date that hasn't passed is still in play, even if
    // it started earlier — rank it by its start so it sits where it belongs.
    const start = e.start_date ? parseDateOnly(e.start_date) : null;
    const end = e.end_date ? parseDateOnly(e.end_date) : null;
    const valid = (d: Date | null): d is Date => d != null && !Number.isNaN(d.getTime());
    if (valid(start) && start >= today) return start.getTime();
    if (valid(end) && end >= today) return valid(start) ? start.getTime() : end.getTime();
    if (valid(start) && valid(end) && start < today && end >= today) return start.getTime();
    return null;
  };

  const hasUsableDate = (e: T): boolean => {
    for (const iso of [e.start_date, e.end_date]) {
      if (!iso) continue;
      if (!Number.isNaN(parseDateOnly(iso).getTime())) return true;
    }
    return false;
  };

  const dated: { e: T; k: number }[] = [];
  const undated: T[] = [];
  for (const e of live) {
    const k = sortKey(e);
    if (k != null) { dated.push({ e, k }); continue; }
    // No sort key and it HAS dates means those dates have all passed — the
    // work is behind us, not unscheduled. Only genuinely undated work is
    // carried through; conflating the two put finished work back on the panel.
    if (hasUsableDate(e)) continue;
    undated.push(e);
  }

  dated.sort((a, b) => a.k - b.k || a.e.task.localeCompare(b.e.task));
  undated.sort((a, b) => a.task.localeCompare(b.task));

  return [...dated.map((d) => d.e), ...undated].slice(0, limit);
}
